// App-level wiring of the plugin system: real grant storage, host-rendered
// consent, a SCOPE-ENFORCED file reader, and toast UI. Installs the first-party
// example plugin. The fs reader gates on BOTH the broker grant (in setup.ts) and
// the host path scope here, so a granted plugin still cannot read outside the
// allowed paths.

import type { PlatformBridge } from '@slate/core';
import type { TrustedKey, RevocationList } from '@slate/plugin-api';
import { createPluginSystem, type PluginSystem } from './setup';
import { localStorageGrantStore } from './localStorageGrantStore';
import {
  localStoragePluginStateStore,
  type PluginStateStore,
} from './localStoragePluginStateStore';
import { consentPrompt } from './consentStore';
import { EXAMPLE_MANIFEST, exampleModule } from './builtinExample';
import { SLATE_CODER_MANIFEST, SLATE_CODER_PLUGIN_ID, slateCoderModule } from './builtinSlateCoder';
import { GIT_MANIFEST, GIT_PLUGIN_ID, gitModule } from './builtinGit';
import type { GrantStore, ConsentPrompt } from './permissionBroker';
import { showModalViaStore } from './surfaceModalStore';
import { createKillSwitch, type KillSwitch } from './integrity/killSwitch';
import { BUNDLED_REVOCATION_LIST } from './integrity/revocationList';
import { createPluginVerifier, type PluginVerifier } from './integrity/verifier';
import { FIRST_PARTY_KEYS, pluginFetchReadFile } from './integrity/trustedKeys';
import { CURATED_PLUGINS } from '../marketplace/source';

/** The plugins that ship installed + enabled in a fresh profile. Persistence is
 *  stored as a DELTA from this set (see localStoragePluginStateStore), and the
 *  install flow reuses it to classify what is a builtin vs an extra. */
export const DEFAULT_BUILTIN_PLUGIN_IDS: readonly string[] = [
  EXAMPLE_MANIFEST.id,
  SLATE_CODER_PLUGIN_ID,
  GIT_PLUGIN_ID,
];

export interface AppPluginDeps {
  platform: PlatformBridge;
  showMessage(message: string): void;
  /** The host's path-scope gate for plugin file reads (e.g. open docs / roots). */
  isPathAllowed(path: string): boolean;
  /** The active editor's text, for `slate.editor.getActiveText()` (ungated). */
  getActiveText?(): string | undefined;
  onCommandRegistered?(pluginId: string, commandId: string): void;
  onViewRegistered?(pluginId: string, viewId: string): void;
  // Overridable for tests:
  store?: GrantStore;
  consent?: ConsentPrompt;
  trustedKeys?: TrustedKey[];
  revocationList?: RevocationList;
  verify?: PluginVerifier;
  killSwitch?: KillSwitch;
  readPluginFile?(pluginId: string, path: string): Promise<Uint8Array>;
  /** Persisted lifecycle delta (installed/enabled state). Defaults to localStorage. */
  pluginStateStore?: PluginStateStore;
}

export function createAppPluginSystem(deps: AppPluginDeps): PluginSystem {
  const system = createPluginSystem({
    store: deps.store ?? localStorageGrantStore,
    consent: deps.consent ?? consentPrompt,
    // Real integrity enforcement: the bundled kill switch refuses revoked ids,
    // and the renderer verifier signature-checks sealed plugins on load. The
    // first-party example is in-process (a module), so the verifier admits it
    // without touching readPluginFile.
    killSwitch: deps.killSwitch ?? createKillSwitch(deps.revocationList ?? BUNDLED_REVOCATION_LIST),
    verify:
      deps.verify ??
      createPluginVerifier({
        trustedKeys: deps.trustedKeys ?? FIRST_PARTY_KEYS,
        readFile: deps.readPluginFile ?? pluginFetchReadFile,
      }),
    fsRead: async (path) => {
      if (!deps.isPathAllowed(path)) {
        throw new Error(`path "${path}" is outside the allowed scope`);
      }
      const file = await deps.platform.readFile(path);
      return file.content;
    },
    getActiveText: deps.getActiveText,
    ui: {
      showMessage: async (message) => {
        deps.showMessage(message);
        return undefined;
      },
      showQuickPick: async () => undefined,
      onCommandRegistered: deps.onCommandRegistered,
      onViewRegistered: deps.onViewRegistered,
      showModal: (pluginId, surfaceId) => showModalViaStore(pluginId, surfaceId),
    },
  });

  // Restore the persisted lifecycle delta from the default builtin set.
  const stateStore = deps.pluginStateStore ?? localStoragePluginStateStore;
  const saved = stateStore.get();

  // Ship the example first-party (trusted tier), so the concept is usable now,
  // UNLESS the user uninstalled it (then it stays in Available, reinstallable).
  if (!saved.uninstalledBuiltins.includes(EXAMPLE_MANIFEST.id)) {
    system.install({ manifest: EXAMPLE_MANIFEST, module: exampleModule });
  }

  // Slate Coder ships installed + enabled and provides code folding. Unlike the
  // command-driven example it must be ACTIVE for its fold providers to register,
  // so activate it eagerly and re-activate whenever it is re-enabled (enabling a
  // plugin does not, by itself, reactivate it).
  if (!saved.uninstalledBuiltins.includes(SLATE_CODER_PLUGIN_ID)) {
    system.install({ manifest: SLATE_CODER_MANIFEST, module: slateCoderModule });
  }

  // Git ships installed + enabled and declares the (revocable) `git` permission.
  // Its UI is the host-registered Source Control panel, not a contributed view,
  // so it does NOT need eager activation; App.tsx gates the panel on this plugin
  // being installed + enabled. Uninstalling sends it back to Available.
  if (!saved.uninstalledBuiltins.includes(GIT_PLUGIN_ID)) {
    system.install({ manifest: GIT_MANIFEST, module: gitModule });
  }

  // Restore extras the user installed beyond the default builtins. These are the
  // SAME bundled, module-backed curated payloads as the builtin seeds (same trust
  // tier), so like those seeds they install without re-running verifyForInstall.
  // A stale id (a plugin no longer in CURATED_PLUGINS) is skipped, and any single
  // failure is contained so it cannot break boot.
  const installedNow = new Set(system.host.list().map((p) => p.manifest.id));
  for (const id of saved.installedExtras) {
    if (DEFAULT_BUILTIN_PLUGIN_IDS.includes(id) || installedNow.has(id)) continue;
    const detail = CURATED_PLUGINS.find((d) => d.id === id);
    if (!detail) continue;
    try {
      system.install(detail.install);
    } catch {
      /* a stale or invalid curated payload must not break boot */
    }
  }

  // Apply persisted-disabled state BEFORE activating Slate Coder, so a plugin the
  // user disabled does not eagerly reactivate on this boot.
  system.host.restoreDisabled(saved.disabled);

  const activateCoder = (): void => {
    const installed = system.host.list().some((p) => p.manifest.id === SLATE_CODER_PLUGIN_ID);
    if (
      installed &&
      system.host.isEnabled(SLATE_CODER_PLUGIN_ID) &&
      !system.host.isActive(SLATE_CODER_PLUGIN_ID)
    ) {
      void system.host.activate(SLATE_CODER_PLUGIN_ID).catch(() => undefined);
    }
  };
  activateCoder();
  system.host.onDidChangeContributions(activateCoder);

  return system;
}
